import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { Board, Move, IPiece, Position, EffectType } from "..\/..\/..\/..\/types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  clonePieceWithPrototype,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

/**
 * Priority 11: King Trade Push
 *
 * When there are race threats and we have blocking pieces in place actively stopping a race victory
 * (either raceblock/raceblockremain or twoturn race defence are actively being applied)
 *
 * The AI must look for: positions that it can threaten the enemy king with a defendable piece (trade)
 * meaning the move will push the king away from a race threat by forcing him to either capture the
 * tradeable piece (giving a free kingslayer victory for our AI) or retreat limiting the threat of
 * racing the enemy has on us, opening up other strategies! (basically like using a check in chess)
 */

export class KingtradepushPriority extends BaseDecisionPriority {
  readonly priority = 14;
  readonly name = "King Trade Push";
  readonly description =
    "Force enemy king retreat with defendable threats when race blocking is active";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    // Only relevant when race blocking is actively happening
    return this.isRaceBlockingActive(context);
  }

  analyze(context: DecisionContext): DecisionResult {
    // Reset logs for this analysis
    this.analysisLogs = [];

    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;
    const enemyColor = isPlayerTurn ? "black" : "white";

    this.addLog(
      `🎯 KING TRADE PUSH: Race blocking is active - looking for king threatening moves`
    );

    // Find the enemy king
    const enemyKing = this.findKing(gameState.board, enemyColor);
    if (!enemyKing) {
      this.addLog(`⚠️ No ${enemyColor} king found`);
      return {
        shouldEarlyExit: false,
        reasoning: `${this.name}: No enemy king found`,
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    this.addLog(
      `👑 Found ${enemyColor} king at [${enemyKing.row},${enemyKing.col}]`
    );

    // Find moves that can threaten the enemy king with defendable pieces
    const kingThreateningMoves = this.findKingThreateningMoves(
      context.allPossibleMoves,
      enemyKing,
      gameState
    );

    if (kingThreateningMoves.length === 0) {
      this.addLog(`❌ No defendable king threatening moves found`);
      return {
        shouldEarlyExit: false,
        reasoning: `${this.name}: No king threatening opportunities`,
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // EARLY EXIT - Force king to move with threatening trades
    this.addLog(
      `⚔️ KING TRADE PUSH: Found ${kingThreateningMoves.length} king threatening moves!`
    );
    this.addLog(
      `🎯 Forcing enemy king to choose: capture (kingslayer) or retreat (reduce race threat)`
    );

    if (kingThreateningMoves.length === 1) {
      return this.createEarlyExitResult(
        kingThreateningMoves[0],
        `${this.name}: FORCED - Threaten king to disrupt race positioning`,
        {
          kingThreats: kingThreateningMoves.length,
          enemyKingPos: enemyKing,
          logs: this.analysisLogs,
        }
      );
    } else {
      return {
        shouldEarlyExit: true,
        filteredMoves: kingThreateningMoves,
        reasoning: `${this.name}: FORCED - Multiple king threatening moves available`,
        priority: this.priority,
        diagnostics: {
          kingThreats: kingThreateningMoves.length,
          enemyKingPos: enemyKing,
          logs: this.analysisLogs,
        },
      };
    }
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  private isRaceBlockingActive(context: DecisionContext): boolean {
    // Check if we're in a race blocking scenario by detecting:
    // 1. Immediate race threats (1-move wins)
    // 2. Defensive race concerns (moves that would open victory paths)
    const gameState = context.gameState;
    const enemyColor = gameState.isLightTurn ? "black" : "white";
    const ourColor = gameState.isLightTurn ? "white" : "black";
    const enemyVictoryRow = enemyColor === "white" ? 7 : 0;

    const moveService = MCTSMoveGenerationService.getInstance();

    // Check 1: Immediate enemy race threats (1-move wins)
    const originalTurn = gameState.isLightTurn;
    gameState.isLightTurn = enemyColor === "white";

    try {
      const enemyMoves = moveService.getAllMoves(gameState);
      const hasImmediateRaceThreats = enemyMoves.some(
        (moveInfo) => moveInfo.move.position.row === enemyVictoryRow
      );

      if (hasImmediateRaceThreats) {
        this.addLog(
          `🚨 Immediate race threats detected - race blocking active`
        );
        return true;
      }
    } finally {
      gameState.isLightTurn = originalTurn;
    }

    // Check 2: Defensive race concerns (moves that would open victory paths)
    // Simulate what Priority 8 (Race Block Remain) would find
    let dangerousMovesCount = 0;

    for (const moveInfo of context.allPossibleMoves) {
      // Simulate making our move
      const testGameState = this.simulateMove(gameState, moveInfo);

      // Check if enemy can reach victory row after our move
      const testOriginalTurn = testGameState.isLightTurn;
      testGameState.isLightTurn = enemyColor === "white";

      try {
        const enemyMovesAfterOurs = moveService.getAllMoves(testGameState);
        const canEnemyWinAfterOurMove = enemyMovesAfterOurs.some(
          (enemyMove) => enemyMove.move.position.row === enemyVictoryRow
        );

        if (canEnemyWinAfterOurMove) {
          dangerousMovesCount++;
        }
      } finally {
        testGameState.isLightTurn = testOriginalTurn;
      }
    }

    // If significant number of moves would open victory paths, race blocking is active
    const dangerousRatio =
      dangerousMovesCount / context.allPossibleMoves.length;
    if (dangerousRatio >= 0.3) {
      // 30% or more moves are dangerous
      this.addLog(
        `🛡️ Defensive race blocking detected - ${dangerousMovesCount}/${context.allPossibleMoves.length} moves would open enemy victory paths`
      );
      return true;
    }

    return false;
  }

  private simulateMove(gameState: any, moveInfo: MoveInfo): any {
    // Create a deep copy of the game state with proper piece prototypes
    const testState = cloneFullGameState(gameState);

    // Apply the move to the test state
    const fromPos = moveInfo.fromPosition;
    const toPos = moveInfo.move.position;

    // Get the piece to move
    const fromSquare = testState.board[fromPos.row][fromPos.col];
    if (fromSquare.Pieces.length === 0) return testState;

    // Create a new piece instance with proper prototype
    const piece = clonePieceWithPrototype(fromSquare.Pieces[0]);

    // Move the piece
    fromSquare.Pieces = [];
    testState.board[toPos.row][toPos.col].Pieces = [piece];

    return testState;
  }

  private findKing(board: any[][], color: "white" | "black"): Position | null {
    const isLight = color === "white";

    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const square = board[row][col];
        if (square.Pieces && square.Pieces.length > 0) {
          const piece = square.Pieces[0];
          if (piece.isLight === isLight && piece.isKing) {
            return { row, col };
          }
        }
      }
    }
    return null;
  }

  private findKingThreateningMoves(
    allMoves: MoveInfo[],
    enemyKing: Position,
    gameState: any
  ): MoveInfo[] {
    const threateningMoves: MoveInfo[] = [];

    for (const moveInfo of allMoves) {
      if (this.canMoveThreatenKing(moveInfo, enemyKing, gameState)) {
        threateningMoves.push(moveInfo);
      }
    }

    return threateningMoves;
  }

  private canMoveThreatenKing(
    moveInfo: MoveInfo,
    enemyKing: Position,
    gameState: any
  ): boolean {
    // Get the piece we're moving
    const ourPiece = this.getPieceAt(gameState.board, moveInfo.fromPosition);
    if (!ourPiece) return false;

    const movePosition = moveInfo.move.position;

    // Check if our piece can threaten the king from its new position
    if (!this.canPieceThreatenPosition(ourPiece, movePosition, enemyKing)) {
      return false;
    }

    // Verify this is a "defendable" threat - can we protect our threatening piece?
    if (this.isThreateningPieceDefendable(moveInfo, gameState)) {
      this.addLog(
        `⚔️ King threat: ${ourPiece.type} to [${movePosition.row},${movePosition.col}] threatens king at [${enemyKing.row},${enemyKing.col}]`
      );
      return true;
    }

    return false;
  }

  private canPieceThreatenPosition(
    piece: IPiece,
    fromPos: Position,
    targetPos: Position
  ): boolean {
    const pieceType = piece.type.toLowerCase();
    const rowDiff = Math.abs(targetPos.row - fromPos.row);
    const colDiff = Math.abs(targetPos.col - fromPos.col);

    // Simplified threat detection based on piece movement patterns
    switch (pieceType) {
      case "bear":
      case "rhino":
        // These pieces can generally threaten adjacent squares
        return rowDiff <= 1 && colDiff <= 1 && (rowDiff > 0 || colDiff > 0);

      case "crane":
        // Crane has specific movement patterns - simplified to adjacent
        return (
          (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1)
        );

      case "monkey":
        // Monkey can leap - more complex, simplified to nearby squares
        return rowDiff <= 2 && colDiff <= 2;

      case "wolf":
        // Wolf can move multiple squares in lines
        return rowDiff === 0 || colDiff === 0 || rowDiff === colDiff;

      default:
        // Conservative approach for other pieces
        return rowDiff <= 1 && colDiff <= 1 && (rowDiff > 0 || colDiff > 0);
    }
  }

  private isThreateningPieceDefendable(
    moveInfo: MoveInfo,
    gameState: any
  ): boolean {
    // Check if we can defend our threatening piece after placing it
    // For now, use a simplified heuristic - pieces that aren't immediately hanging

    const targetSquare =
      gameState.board[moveInfo.move.position.row][moveInfo.move.position.col];

    // If the target square is empty, it's more likely to be defendable
    if (!targetSquare.Pieces || targetSquare.Pieces.length === 0) {
      return true;
    }

    // If we're capturing something, it might be worth the trade
    const ourPiece = this.getPieceAt(gameState.board, moveInfo.fromPosition);
    const enemyPiece = targetSquare.Pieces[0];

    if (ourPiece && enemyPiece) {
      // Simple piece value comparison - if we're trading equal or up, it's defendable
      const ourValue = this.getPieceValue(ourPiece.type);
      const enemyValue = this.getPieceValue(enemyPiece.type);
      return ourValue <= enemyValue;
    }

    return true; // Default to defendable
  }

  private getPieceValue(pieceType: string): number {
    // Simplified piece values for trade evaluation
    switch (pieceType.toLowerCase()) {
      case "monkey":
        return 1;
      case "goat":
        return 2;
      case "crane":
        return 3;
      case "bear":
        return 5;
      case "rhino":
        return 5;
      case "wolf":
        return 4;
      case "tree":
        return 3;
      case "octopus":
        return 4;
      case "penguin":
        return 2;
      case "virus":
        return 2;
      case "fungi":
        return 3;
      default:
        return 3;
    }
  }

  private getPieceAt(board: any[][], position: Position): IPiece | null {
    if (
      position.row < 0 ||
      position.row >= board.length ||
      position.col < 0 ||
      position.col >= board[position.row].length
    ) {
      return null;
    }

    const square = board[position.row][position.col];
    if (!square || !square.Pieces || square.Pieces.length === 0) {
      return null;
    }

    return square.Pieces[0];
  }
}

